import { commonSuggestions } from "libs/datetime-suggestions/commonSuggestions";
import { CommonSuggestionProps } from "libs/datetime-suggestions/commonSuggestions";
import { DateTimeSuggestion } from "libs/datetime-suggestions/DateTimeSuggestion";
import { dueDateRuleSuggestions } from "libs/datetime-suggestions/dueDateRuleSuggestions";
import { matchDateTimeSuggestions } from "libs/datetime-suggestions/matchDateTimeSuggestions";
import { RecordValue, RelativeDueDate } from "libs/schema";
import { ClientEnvironment } from "~/environment/ClientEnvironment";

export function getDueDateSuggestions(
  environment: Pick<ClientEnvironment, "logger">,
  props: {
    query: string;
    threadRequest: RecordValue<"thread_request"> | null;
    defaultDueDateSuggestion: RelativeDueDate | null;
  },
) {
  const suggestions = innerGetDueDateSuggestions(environment, props);

  return matchDateTimeSuggestions({
    input: props.query,
    suggestions,
    limit: 5,
  });
}

// Note, the order of these suggestions matters because, *all
// other things being equal*, suggestions will be displayed
// in this order here.
// In general, suggestions should be ordered from simpler to
// more complex and from most common to least common.
function innerGetDueDateSuggestions(
  environment: Pick<ClientEnvironment, "logger">,
  props: {
    threadRequest: RecordValue<"thread_request"> | null;
    defaultDueDateSuggestion: RelativeDueDate | null;
  },
) {
  const commonSuggestionProps: CommonSuggestionProps = {
    defaultTimeForSuggestion: "EOP",
    weekStartsOn: "mo",
    weekendStartsOn: "sa",
  };

  const suggestions = Object.values(commonSuggestions).map((factory) => {
    const suggestion = factory(commonSuggestionProps);
    return suggestion;
  });

  const dueDateSuggestions: DateTimeSuggestion[] = [];

  const hasOpenRequest = props.threadRequest && !props.threadRequest.resolved_at;

  // The first 5 suggestions are also shown when the user
  // hasn't typed anything. These are the "default" suggestions.
  if (hasOpenRequest) {
    dueDateSuggestions.push(
      new DateTimeSuggestion({
        id: "requestDone",
        template: "request done",
        keywords: ["Done"],
        valueType: null,
        getDate: () => null,
      }),
    );
  }

  const defaultDueDateSuggestion = getDefaultDueDateSuggestion(environment, {
    defaultDueDateSuggestion: props.defaultDueDateSuggestion,
    commonSuggestionProps,
  });

  // Other default suggestions
  dueDateSuggestions.push(
    defaultDueDateSuggestion,
    commonSuggestions.tomorrow(commonSuggestionProps),
    commonSuggestions.nextWeek(commonSuggestionProps),
    commonSuggestions.eod(commonSuggestionProps),
    commonSuggestions.someday(commonSuggestionProps),
    ...suggestions,
  );

  return dueDateSuggestions;
}

function getDefaultDueDateSuggestion(
  environment: Pick<ClientEnvironment, "logger">,
  props: {
    defaultDueDateSuggestion: RelativeDueDate | null;
    commonSuggestionProps: CommonSuggestionProps;
  },
) {
  if (!props.defaultDueDateSuggestion) {
    return getGlobalDefaultDueDateSuggestion();
  }

  const suggestionFactory =
    dueDateRuleSuggestions[props.defaultDueDateSuggestion.id as keyof typeof dueDateRuleSuggestions];

  if (!suggestionFactory) {
    environment.logger.error(
      { defaultDueDateSuggestion: props.defaultDueDateSuggestion },
      `[getDefaultDueDateSuggestion] invalid default due date suggestion`,
    );

    return getGlobalDefaultDueDateSuggestion();
  }

  const suggestion = suggestionFactory(props.commonSuggestionProps);

  // We override the id to create a new, unique suggestion.
  // We also override the template and content to hardcode this specific suggestion.
  // This is safe because we're only using this suggestion to get a specific due date
  // for a mention--we're not saving the suggestion template the due date came from.
  suggestion.id = `defaultSuggestion`;
  suggestion.template = props.defaultDueDateSuggestion.content;
  suggestion.content = props.defaultDueDateSuggestion.content;
  suggestion.contentVariables = structuredClone(props.defaultDueDateSuggestion.contentVariables);

  return suggestion;
}

const getGlobalDefaultDueDateSuggestion = () =>
  new DateTimeSuggestion({
    id: `defaultSuggestion`,
    template: `2 days from now`,
    valueType: "date",
    getDate: DateTimeSuggestion.defaultGetDateFn((date) => date.endOf("day")),
  });
